import UiRouterModule from "@uirouter/angularjs";

import LoadBasicApiDataError from "./LoadBasicApiDataError";

import AuthSvcModule from "tbClient/services/auth";
import AllOfficeTypesServiceModule from "tbClient/services/AllOfficeTypesService";
import AllStatesServiceModule from "tbClient/services/AllStatesService";
import AllEventTypesServiceModule from "tbClient/services/AllEventTypesService";
import TbAppConfigDataServiceModule from "tbClient/config/tb-app-config/TbAppConfigDataService";
import UserInfoServiceModule from "tbClient/services/UserInfoService";
import EventFilterStoreServiceModule from "tbClient/services/EventFilterStoreService";
import AppOverlayComponentModule from "tbClient/directives/app-overlay";

import ErrorPageViewModuleConstants from "tbClient/views/error-page/Constants";

import DependencyListBuilder from "tbClient/utility/DependencyListBuilder";

const stateRequiresAuthCriteria = {
    to: (state) => state.data && (state.data.requiresLogin === true)
};

function ConfigureLoadApiBasicDataHook($transitions, $log) {

    const showLoadingOverlay = (transition) => {
        const appOverlaySvc = transition.injector().get("AppOverlay");
        const timeoutSvc = transition.injector().get("$timeout");

        return timeoutSvc(() => {
            appOverlaySvc.overlayMessage = "Loading";
            appOverlaySvc.overlayType = "primary";
            appOverlaySvc.showOverlay = true;
        },0);
    };

    const hideLoadingOverlay = (transition) => {
        const appOverlaySvc = transition.injector().get("AppOverlay");
        const timeoutSvc = transition.injector().get("$timeout");

        return timeoutSvc(() => {
            appOverlaySvc.showOverlay = false;
        },0);
    };

    const loadOfficeTypesData = (transition) => {
        const allOfficeTypesSvc = transition.injector().get("AllOfficeTypes");

        $log.debug("Starting Load Office Types Data Hook");
        const loadOfficeTypesDataPromise = allOfficeTypesSvc.PreLoadOfficeTypesData()
                                                 .then(() => {
                                                     $log.debug("Post-authentication: Successfully loaded office types data.");
                                                 })
                                                 .catch((err) => {
                                                     throw new LoadBasicApiDataError({
                                                         errorMessage: "Post-authentication: office types data failed to load",
                                                         errorStepName: "Office types data load",
                                                         errorData: err
                                                     });
                                                 });

        return loadOfficeTypesDataPromise;
    };

    const loadStateData = (transition) => {
        const allStatesSvc = transition.injector().get("AllStatesService");

        $log.debug("Starting Load State Data Hook");
        const loadStateDataPromise = allStatesSvc.loadRemoteStateData()
            .then(() => {
                $log.debug("Post-authentication: Successfully loaded state data.");
            })
            .catch((err) => {
                throw new LoadBasicApiDataError({
                    errorMessage: "Post-authentication: state data failed to load",
                    errorStepName: "States data load",
                    errorData: err
                });
            });

        return loadStateDataPromise;
    };

    const loadEventTypesDataAndDependentServices = (transition) => {
        const allEventTypesSvc = transition.injector().get("AllEventTypesService");
        const calendarGlobalViewSvc = transition.injector().get("CalendarGlobalViewService");

        $log.debug("Starting Load Event Type Data Hook");
        const loadAllEventTypesDataPromise = allEventTypesSvc.PreLoadEventTypesData()
            .then(() => {
                calendarGlobalViewSvc.reload();
            })
            .then(() => {
                 $log.debug("Post-authentication: Successfully loaded event type data and reloaded dependent services.");
            })
            .catch((err) => {
                throw new LoadBasicApiDataError({
                    errorMessage: "Post-authentication: event types data failed to load",
                    errorStepName: "Event types data load",
                    errorData: err
                });
            });

        return loadAllEventTypesDataPromise;
    };

    const reloadUserInfoAndEventFilterStore = (transition) => {

        const appConfigDataSvc = transition.injector().get("TbAppConfigDataService");
        const userInfoSvc = transition.injector().get("UserInfoService");
        const eventFilterStoreSvc = transition.injector().get("EventFilterStore");

        $log.debug("Starting Reload User Info and Event Filter Store Hook");
        const userInfoReloadPromise = userInfoSvc.reload()
             .then(() => {
                if (appConfigDataSvc.tbEnableDebugMode) {
                    userInfoSvc.logUserRights();
                }
                eventFilterStoreSvc.reload();
            })
            .catch((err) => {
                throw new LoadBasicApiDataError({
                    errorMessage: "Error loading user information and stored event filters",
                    errorStepName: "User Info-Event Filters data load",
                    errorData: err
                });
            });

        return userInfoReloadPromise;
    };

    const LoadApiBasicDataHook = (transition) => {
        return showLoadingOverlay(transition)
            .then(() => {
                const loadOfficeTypesDataPromise = loadOfficeTypesData(transition);
                const loadStateDataPromise = loadStateData(transition);
                const loadEventTypesDataPromise = loadEventTypesDataAndDependentServices(transition);
                const loadUserInfoAndEventFilterStorePromise = reloadUserInfoAndEventFilterStore(transition);

                return Promise.all([
                    loadOfficeTypesDataPromise,
                    loadStateDataPromise,
                    loadEventTypesDataPromise,
                    loadUserInfoAndEventFilterStorePromise
                ]);
            })
            .then(() => {
                $log.debug("Finished loading core application data.");
                return true;
            })
            .catch((err) => {
                if (err instanceof LoadBasicApiDataError) {
                    $log.error("An error occurred while loading core application data.", err);
                    return transition.router.stateService.target(ErrorPageViewModuleConstants.rootStateName);
                }
                else {
                    $log.debug("An unknown error occurred while loading core application data.");
                    throw err;
                }
            })
            .finally(() => hideLoadingOverlay(transition));
    };

    const hookOptions = {
        priority: 10,
        invokeLimit: 1
    };

    $transitions.onEnter(stateRequiresAuthCriteria, LoadApiBasicDataHook, hookOptions);
}

ConfigureLoadApiBasicDataHook.$inject = ["$transitions", "$log"];

ConfigureLoadApiBasicDataHook.dependencies = DependencyListBuilder([
    [
        UiRouterModule,
        AuthSvcModule,
        AllOfficeTypesServiceModule,
        AllStatesServiceModule,
        AllEventTypesServiceModule,
        TbAppConfigDataServiceModule,
        UserInfoServiceModule,
        EventFilterStoreServiceModule,
        AppOverlayComponentModule
    ]
]);

export default ConfigureLoadApiBasicDataHook;





//////////////////
// WEBPACK FOOTER
// ./src/hooks/LoadBasicApiDataHook.js
// module id = W7cZ
// module chunks = 1